import { CreditCard, DollarSign, Download, LogOut, Printer, Receipt, Share2, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import PrintableBill from '../components/billing/PrintableBill';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import ordersService from '../services/ordersService';
import { theme } from '../styles/theme';
import { formatBillData, generatePDF } from '../utils/printUtils';

const Container = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.lg};
  padding-bottom: 80px;
  position: relative;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};

  h1 {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    h1 {
      font-size: ${theme.typography.fontSize.xl};
    }
  }
`;

const LogoutButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${theme.spacing.lg};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const OrdersList = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const OrdersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const OrderCard = styled(Card)`
  margin-bottom: ${theme.spacing.md};
  cursor: pointer;
  border: 3px solid ${({ selected }) => selected ? theme.colors.primary : 'transparent'};
  background-color: ${({ selected }) => selected ? `${theme.colors.primary}10` : theme.colors.white};

  &:hover {
    border-color: ${theme.colors.primary};
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};

  div {
    h3 {
      font-size: ${theme.typography.fontSize.lg};
      font-weight: ${theme.typography.fontWeight.semiBold};
      color: ${theme.colors.text};
      margin-bottom: ${theme.spacing.xs};
    }

    p {
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.textSecondary};
    }
  }

  span {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
  }
`;

const OrderItems = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};

  &:last-child {
    margin-bottom: 0;
  }
`;

const BillPanel = styled.div`
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.md};
  position: sticky;
  top: ${theme.spacing.lg};
  max-height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
`;

const BillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const BillDetails = styled.div`
  flex: 1;
  margin-bottom: ${theme.spacing.lg};
`;

const BillRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};

  ${({ highlight }) => highlight && `
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
    margin-top: ${theme.spacing.md};
    padding-top: ${theme.spacing.md};
    border-top: 2px solid ${theme.colors.border};
  `}
`;

const DiscountSection = styled.div`
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.secondary};
  border-radius: ${theme.borderRadius.md};
`;

const DiscountInput = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.sm};
`;

const PaymentButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
`;

const PaymentButton = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.lg};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  border: 2px solid ${theme.colors.border};
  background-color: ${theme.colors.white};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${theme.typography.fontWeight.semiBold};

  &:hover {
    background-color: ${theme.colors.secondary};
    border-color: ${theme.colors.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['3xl']};
  color: ${theme.colors.textSecondary};
`;

// Orders are loaded inside the CounterBilling component (hooks & fetch placed inside the component)

function CounterBilling() {
  const navigate = useNavigate();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [discountInput, setDiscountInput] = useState('');
  const printRef = useRef();

  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);

  useEffect(() => {
    fetchOrders();
    // optional: poll for updates
    // const interval = setInterval(fetchOrders, 5000);
    // return () => clearInterval(interval);
  }, []);

  async function fetchOrders() {
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      const data = await ordersService.getAll();
      // Filter orders that are ready for billing (ready_for_billing status means sent from waiter)
      const readyOrders = (Array.isArray(data) ? data : []).filter(
        order => order.status === 'ready_for_billing' || order.status === 'completed'
      );
      setOrders(readyOrders);
    } catch (err) {
      setOrdersError(err.message || 'Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  }

  // Refresh orders periodically
  useEffect(() => {
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    navigate('/login');
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setDiscount(parseFloat(order.discount || 0));
    setDiscountInput(order.discount ? order.discount.toString() : '');
  };

  const handleApplyDiscount = async () => {
    if (!selectedOrder) return;
    const discountValue = parseFloat(discountInput);
    if (!isNaN(discountValue) && discountValue >= 0) {
      setDiscount(discountValue);
      
      // Update order with discount in backend
      try {
        const subtotal = calculateSubtotal();
        const gst = subtotal * 0.18;
        const newTotal = subtotal + gst - discountValue;
        
        const updatedOrder = await ordersService.update(selectedOrder.id, {
          discount: discountValue,
          total: newTotal
        });
        
        // Update selected order with new data
        setSelectedOrder(updatedOrder);
        
        // Refresh orders list
        await fetchOrders();
      } catch (error) {
        console.error('Error applying discount:', error);
        alert('Failed to apply discount. Please try again.');
      }
    } else {
      alert('Please enter a valid discount amount');
    }
  };

  const handlePayment = async (method) => {
    if (!selectedOrder) return;
    try {
      // mark order as completed on the server
      await ordersService.update(selectedOrder.id, { status: 'completed' });
      alert(`Payment of ₹${calculateTotal()} processed via ${method}`);
      setSelectedOrder(null);
      await fetchOrders();
    } catch (err) {
      alert(err.message || 'Payment failed');
    }
  };

  const calculateSubtotal = () => {
    if (!selectedOrder) return 0;
    // Handle both API format and local format
    if (selectedOrder.items && selectedOrder.items.length > 0) {
      return selectedOrder.items.reduce((sum, item) => {
        const price = parseFloat(item.price || 0);
        const quantity = parseInt(item.quantity || item.qty || 0);
        return sum + (price * quantity);
      }, 0);
    }
    return parseFloat(selectedOrder.subtotal || 0);
  };

  const calculateGST = () => {
    if (!selectedOrder) return 0;
    // Use stored GST if available, otherwise calculate
    if (selectedOrder.gst !== undefined) {
      return parseFloat(selectedOrder.gst || 0);
    }
    return calculateSubtotal() * 0.18;
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const gst = calculateGST();
    return subtotal + gst - discount;
  };

  // Print handler using react-to-print
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Bill-${selectedOrder?.id || 'N/A'}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 10px;
        }
      }
    `,
  });

  // Handle print button click
  const handlePrintClick = () => {
    if (!selectedOrder) {
      alert('Please select an order to print');
      return;
    }
    handlePrint();
  };

  // Handle PDF download
  const handleDownloadPDF = async () => {
    if (!selectedOrder) {
      alert('Please select an order to download');
      return;
    }
    const billData = formatBillData(
      selectedOrder,
      discount,
      calculateSubtotal,
      calculateGST,
      calculateTotal
    );
    await generatePDF(billData, `Bill-${selectedOrder.id}.pdf`);
  };

  // Show orders that are ready for billing (ready_for_billing status means sent from waiter)
  const readyOrders = orders.filter((order) => order.status === 'ready_for_billing' || order.status === 'completed');

  return (
    <Container>
      <Header>
        <h1>Counter / Billing</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
          <span style={{ color: theme.colors.textSecondary }}>
            {loadingOrders ? 'Loading orders...' : `${readyOrders.length} orders ready for billing`}
          </span>
          <LogoutButton variant="secondary" size="sm" onClick={handleLogout}>
            <LogOut size={18} />
            Logout
          </LogoutButton>
        </div>
      </Header>

      <ContentGrid>
        <OrdersList>
          <OrdersHeader>
            <h2>Ready Orders</h2>
          </OrdersHeader>

          {loadingOrders ? (
            <EmptyState>
              <p>Loading orders...</p>
            </EmptyState>
          ) : ordersError ? (
            <EmptyState>
              <p>{ordersError}</p>
            </EmptyState>
          ) : readyOrders.length === 0 ? (
            <EmptyState>
              <p>No orders ready for billing</p>
            </EmptyState>
          ) : (
            readyOrders.map((order) => (
              <OrderCard
                key={order.id}
                selected={selectedOrder?.id === order.id}
                onClick={() => handleSelectOrder(order)}
              >
                <OrderHeader>
                  <div>
                    <h3>Table {order.tableNumber}</h3>
                    <p>
                      {order.waiterName ? `Waiter: ${order.waiterName} • ` : ''}
                      {order.orderNumber || order.id}
                      {order.createdAt ? ` • ${new Date(order.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                  <span>₹{parseFloat(order.total || 0).toFixed(2)}</span>
                </OrderHeader>
                <OrderItems>
                  {(order.items || []).map((item, index) => (
                    <OrderItem key={index}>
                      <span>
                        {item.quantity || item.qty || 0}x {item.itemName || item.name || 'Item'}
                      </span>
                      <span>₹{((parseFloat(item.price || 0)) * (item.quantity || item.qty || 1)).toFixed(2)}</span>
                    </OrderItem>
                  ))}
                </OrderItems>
              </OrderCard>
            ))
          )}
        </OrdersList>

        <BillPanel>
          <BillHeader>
            <Receipt size={24} />
            <h2>Bill Summary</h2>
          </BillHeader>

          {selectedOrder ? (
            <>
              <BillDetails>
                <BillRow>
                  <span>Table: {selectedOrder.tableNumber}</span>
                  <span>{selectedOrder.waiterName ? `Waiter: ${selectedOrder.waiterName}` : selectedOrder.orderNumber}</span>
                </BillRow>

                <div style={{ marginTop: theme.spacing.lg, marginBottom: theme.spacing.lg }}>
                  {(selectedOrder.items || []).map((item, index) => {
                    const itemName = item.itemName || item.name || 'Item';
                    const quantity = item.quantity || item.qty || 0;
                    const price = parseFloat(item.price || 0);
                    const itemTotal = price * quantity;
                    return (
                      <BillRow key={index}>
                        <span>{quantity}x {itemName}</span>
                        <span>₹{itemTotal.toFixed(2)}</span>
                      </BillRow>
                    );
                  })}
                </div>

                <BillRow>
                  <span>Subtotal:</span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </BillRow>
                <BillRow>
                  <span>GST (18%):</span>
                  <span>₹{calculateGST().toFixed(2)}</span>
                </BillRow>

                <DiscountSection>
                  <label style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semiBold }}>
                    Discount
                  </label>
                  <DiscountInput>
                    <Input
                      type="number"
                      placeholder="Enter discount"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                    />
                    <Button variant="outline" size="sm" onClick={handleApplyDiscount}>
                      Apply
                    </Button>
                  </DiscountInput>
                  {discount > 0 && (
                    <BillRow style={{ marginTop: theme.spacing.sm }}>
                      <span>Discount:</span>
                      <span>-₹{discount.toFixed(2)}</span>
                    </BillRow>
                  )}
                </DiscountSection>

                <BillRow highlight>
                  <span>Total:</span>
                  <span>₹{calculateTotal().toFixed(2)}</span>
                </BillRow>
              </BillDetails>

              <PaymentButtons>
                <PaymentButton variant="primary" onClick={() => handlePayment('Cash')}>
                  <DollarSign />
                  <span>Cash</span>
                </PaymentButton>
                <PaymentButton variant="accent" onClick={() => handlePayment('UPI')}>
                  <Smartphone />
                  <span>UPI</span>
                </PaymentButton>
                <PaymentButton variant="primary" onClick={() => handlePayment('Card')}>
                  <CreditCard />
                  <span>Card</span>
                </PaymentButton>
                <PaymentButton variant="secondary" onClick={() => handlePayment('Other')}>
                  <span>Other</span>
                </PaymentButton>
              </PaymentButtons>

              <ActionButtons>
                <ActionButton onClick={handlePrintClick}>
                  <Printer />
                  <span>Print</span>
                </ActionButton>
                <ActionButton onClick={handleDownloadPDF}>
                  <Download />
                  <span>PDF</span>
                </ActionButton>
                <ActionButton onClick={() => alert('Sharing bill...')}>
                  <Share2 />
                  <span>Share</span>
                </ActionButton>
              </ActionButtons>
            </>
          ) : (
            <EmptyState>
              <p>Select an order to view bill</p>
            </EmptyState>
          )}
        </BillPanel>
      </ContentGrid>

      {/* Hidden printable bill component */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {selectedOrder && (
            <PrintableBill
              billData={formatBillData(
                selectedOrder,
                discount,
                calculateSubtotal,
                calculateGST,
                calculateTotal
              )}
            />
          )}
        </div>
      </div>

      <Footer hasSidebar={false} />
    </Container>
  );
}

export default CounterBilling;

